import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BoardRef } from '../../models/boards/board-ref.enum';
import { IBoard } from '../../models/boards/board.interface';
import { FatalError } from '../../models/core/fatal-error.model';
import {
  UPDATE_USER_INFO_MUTATION,
  UPDATE_USER_PW_MUTATION,
} from '../../models/core/graphql.mutations';
import {
  ADMIN_ACCESS_QUERY,
  BOARDS_NG_ACCESS,
  CENSUS_NG_ACCESS,
  CENSUS_NG_SMART_ACCESS,
  CURRENT_USER_BOARDS_QUERY,
  CURRENT_USER_QUERY,
  OLD_CURRENT_USER_QUERY,
  REALTIME_REPORTS_NG_ACCESS,
  REPORTS_NG_ACCESS,
  UNPUBLISHED_TOPLIST_NG_ACCESS,
  USER_QUERY,
} from '../../models/core/graphql.query';
import { IAccountPref } from '../../models/identity/account-pref.interface';
import { IUser } from '../../models/identity/user.interface';
import { IGraphQLResponse } from '../../models/results/graphql-response.interface';
import { GraphqlService } from '../common/graphql.service';

/**
 * Service that manages user related logic and operations.
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  /**
   * Gets or sets token from old user.
   * @deprecated This property is used to get old dashboard data.
   */
  private oldToken: string | undefined;

  /**
   * Gets or sets if service has been fully initialized.
   */
  public isInitialized: boolean = false;

  /**
   * Gets or sets currently signed user.
   */
  public currentUser: IUser | undefined;

  /**
   * Gets or sets starred boards of currenly signed user.
   */
  public currentUserStarredBoards: IBoard[] | undefined;

  /**
   * Gets or sets recently visited boards of currenly signed user.
   */
  public currentUserRecentBoards: IBoard[] | undefined;

  /**
   * Gets or sets if user has access to admin panel.
   */
  public hasAdminAccess: boolean | undefined;

  constructor(
    private httpClient: HttpClient,
    private graphQL: GraphqlService
  ) {}

  /**
   * Initializes service. This method is called from guard that stalls loading of other components
   * in case this service is not fully initialized.
   */
  public async initialize(): Promise<void> {
    this.currentUser = await this.getCurrentUser();
    this.currentUserStarredBoards = await this.getStarredBoardsFromUser();
    this.hasAdminAccess = await this.hasAdminPanelAccess();

    this.isInitialized = true;
  }

  /**
   * Gets currently signed user.
   */
  private async getCurrentUser(): Promise<IUser> {
    let result = await this.graphQL.query<IGraphQLResponse<IUser>>(
      CURRENT_USER_QUERY
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      Promise.reject(
        new FatalError(
          'Could not get current user. ' + result.data?.response.message
        )
      );
    }

    this.oldToken = await this.getOldCurrentUser();
    return result.data?.response.data!;
  }

  /**
   * Gets starred boards of the currenly signed user.
   */
  private async getStarredBoardsFromUser(): Promise<IBoard[]> {
    let result = await this.graphQL.query<IGraphQLResponse<IBoard[]>>(
      CURRENT_USER_BOARDS_QUERY,
      {
        starred: true,
      }
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      Promise.reject(new FatalError('Could not get boards of the user.'));
    }

    return result.data?.response.data!;
  }

  /**
   * Gets old currently signed user.
   * @deprecated This method is used to get old user token.
   */
  public async getOldCurrentUser(): Promise<string> {
    let result = await this.graphQL.query<IGraphQLResponse<string>>(
      OLD_CURRENT_USER_QUERY,
      {}
    );
    let token = result.data?.response.data;
    return token!;
  }

  /**
   * Gets if user has access to admin panel.
   */
  public async hasAdminPanelAccess(): Promise<boolean> {
    let result = await this.graphQL.query<IGraphQLResponse<boolean>>(
      ADMIN_ACCESS_QUERY
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.isSuccess == false
    ) {
      return false;
    }

    return true;
  }

  /**
   * Checks if given board is saved in starred, recent or external repository.
   * @param id
   */
  public getBoardReference(id: string): BoardRef | undefined {
    if (this.currentUserStarredBoards?.find((x) => x.id == id) != null) {
      return BoardRef.Starred;
    }

    if (this.currentUserRecentBoards?.find((x) => x.id == id) != null) {
      return BoardRef.Recent;
    }

    return undefined;
  }

  /**
   * Gets info of target user.
   */
  public async getUserInfoById(userId: string): Promise<IUser> {
    let result = await this.graphQL.query<IGraphQLResponse<IUser>>(USER_QUERY, {
      targetUserId: userId,
    });
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      Promise.reject(
        new FatalError(`Could not get info of the user user ${userId}.`)
      );
    }

    return result.data?.response.data!;
  }

  /**
   * Gets list of old dashboards.
   * @deprecated This method is used to get old dashboard data.
   */
  public getOldDashboards(): Promise<any> {
    let url =
      'https://evoweb.at.evo.gfk.com/streamingDashboardNext/api/v2/users/current/dashboards?culture=en';
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.oldToken,
    });

    return new Promise<any>((resolve) => {
      this.httpClient.get(url, { headers: httpHeaders }).subscribe(
        (response) => {
          resolve(<any>response);
        },
        (error) => {
          resolve(null);
        }
      );
    });
  }

  /**
   * Updates info of target user.
   */
  public async updateUserInfo(info: IAccountPref): Promise<boolean> {
    let result = await this.graphQL.mutate<IGraphQLResponse<boolean>>(
      UPDATE_USER_INFO_MUTATION,
      {
        info: info,
      }
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      //Promise.reject(new FatalError(`Could not update user information.`));
      return false;
    }

    return result.data?.response.data!;
  }

  /**
   * Updates password of target user.
   */
  public async updatePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<boolean> {
    let result = await this.graphQL.mutate<IGraphQLResponse<boolean>>(
      UPDATE_USER_PW_MUTATION,
      {
        oldPassword: oldPassword,
        newPassword: newPassword,
      }
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      //Promise.reject(new FatalError(`Could not update user information.`));
      return false;
    }

    return result.data?.response.data!;
  }

  /**
   * Gets available data days for census.
   */
  public async getBoardsAccess(): Promise<boolean> {
    let result = await this.graphQL.query<IGraphQLResponse<boolean>>(
      BOARDS_NG_ACCESS,
      {}
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      return false;
    }

    return result.data?.response.data!;
  }

  /**
   * Gets available data days for census.
   */
  public async getReportsAccess(): Promise<boolean> {
    let result = await this.graphQL.query<IGraphQLResponse<boolean>>(
      REPORTS_NG_ACCESS,
      {}
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      return false;
    }

    return result.data?.response.data!;
  }

  /**
   * Gets available data days for census.
   */
  public async getRealtimeReportsAccess(): Promise<boolean> {
    let result = await this.graphQL.query<IGraphQLResponse<boolean>>(
      REALTIME_REPORTS_NG_ACCESS,
      {}
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      return false;
    }

    return result.data?.response.data!;
  }

  /**
   * Gets available data days for census.
   */
  public async getCensusAccess(): Promise<boolean> {
    let result = await this.graphQL.query<IGraphQLResponse<boolean>>(
      CENSUS_NG_ACCESS,
      {}
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      return false;
    }

    return result.data?.response.data!;
  }

  /**
   * Gets available data days for census.
   */
  public async getCensusSmartAccess(): Promise<boolean> {
    let result = await this.graphQL.query<IGraphQLResponse<boolean>>(
      CENSUS_NG_SMART_ACCESS,
      {}
    );
    if (
      result.data == undefined ||
      result.data.response == undefined ||
      result.data.response.data == undefined ||
      result.data.response.isSuccess == false
    ) {
      return false;
    }

    return result.data?.response.data!;
  }
}
